import React, { forwardRef, useId } from 'react'

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    label?: string
    error?: string
    helperText?: string
    leftIcon?: React.ReactNode
    size?: 'sm' | 'md' | 'lg'
}

const sizeStyles = {
    sm: {
        input: 'pr-2.5 py-1.5 text-sm',
        plNormal: 'pl-2.5',
        plWithIcon: 'pl-9',
        label: 'text-xs',
        icon: 'pl-2.5',
        message: 'text-xs',
    },
    md: {
        input: 'pr-3 py-2 text-sm',
        plNormal: 'pl-3',
        plWithIcon: 'pl-10',
        label: 'text-sm',
        icon: 'pl-3',
        message: 'text-sm',
    },
    lg: {
        input: 'pr-4 py-4 text-2xl min-h-[4rem]',
        plNormal: 'pl-4',
        plWithIcon: 'pl-16',
        label: 'text-lg',
        icon: 'pl-5',
        message: 'text-base',
    },
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ className = '', label, error, helperText, leftIcon, size = 'md', ...props }, ref) => {
        const inputId = props.id ?? useId()
        const styles = sizeStyles[size]
        return (
            <div className="w-full">
                {label && (
                    <label
                        htmlFor={inputId}
                        className={`block font-medium text-slate-700 dark:text-slate-300 mb-2 ${styles.label}`}
                    >
                        {label}
                    </label>
                )}
                <div className="relative">
                    {leftIcon && (
                        <div
                            className={`absolute inset-y-0 left-0 ${styles.icon} flex items-center pointer-events-none text-slate-400 dark:text-slate-500`}
                        >
                            {leftIcon}
                        </div>
                    )}
                    <input
                        ref={ref}
                        id={inputId}
                        className={`
              block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700
              text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500
              focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20
              disabled:cursor-not-allowed disabled:bg-slate-50 dark:disabled:bg-slate-800 disabled:text-slate-500
              ${styles.input}
              ${leftIcon ? styles.plWithIcon : styles.plNormal}
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}
              ${className}
            `}
                        {...props}
                    />
                </div>
                {error && <p className={`mt-1.5 text-red-600 dark:text-red-400 ${styles.message}`}>{error}</p>}
                {helperText && !error && <p className={`mt-1.5 text-slate-500 dark:text-slate-400 ${styles.message}`}>{helperText}</p>}
            </div>
        )
    }
)

Input.displayName = 'Input'
